import { IPatientVitals, IVitalsAction, IPainQuality, ISedationScale } from './vitals.types'
import { VitalsConstants } from './vitals.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../utilities'
import { IOption } from '../../../common/common.types'

export const initialPainQualityState: IPainQuality = {
  onset: null,
  onsetUnit: null,
  field1: null,
  field2: null,
  field3: null,
  field4: null,
  field5: null,
  field6: null,
  field7: null,
  comments: '',
}

export const initialSedationScaleState: ISedationScale = {
  totalsCombined: '',
  selections: [],
}

export const initialVitalsState: IPatientVitals = {
  key: randomNumber().toString(),
  pta: false,
  date: '',
  time: '',
  assessedBy: null,
  comments: '',
  systolicBP: null,
  systolicBPPNNV: null,
  diastolicBP: null,
  diastolicBPPNNV: null,
  bloodPressureMethod: null,
  bloodPressureMethodPNNV: null,
  mapVital: null,
  heartRate: null,
  heartRatePNNV: null,
  heartRateMethod: null,
  spo2: null,
  spo2PNNV: null,
  roomAir: false,
  respirationRate: null,
  respirationRatePNNV: null,
  respirationRateEffort: null,
  rhythm: [],
  rhythmPNNV: null,
  regularity: null,
  ecgType: null,
  ecgTypeMethod: [],
  ecgTypeMethodPNNV: null,
  temperature: null,
  temperaturePNNV: null,
  temperatureType: '',
  temperatureMethod: null,
  spCO: null,
  spCOPNNV: null,
  spMet: null,
  rts: null,
  etCO2: null,
  etCO2PNNV: null,
  glucose: null,
  glucosePNNV: null,
  glucoseLevel: 'Low',
  levelOfConsciousness: null,
  pain: '',
  painPNNV: null,
  painType: null,
  painScales: { totalsCombined: '', painScaleSelections: [] },
  strokeScale: null,
  strokeScalePNNV: null,
  strokeScoreFlag: false,
  strokeScaleScoreTotal: null,
  vitalsStrokeScaleValues: [],
  strokeScaleScoreTotalPNNV: null,
  __isStrokeScaleScoreTotalPNNVEnabled: false,
  eyeOpeningResponse: null,
  eyeOpeningResponsePNNV: null,
  verbalResponse: null,
  verbalResponsePNNV: null,
  motorResponse: null,
  motorResponsePNNV: null,
  protocol: null,
  totalGCSScore: null,
  totalGCSScorePNNV: null,
  eyeOpeningResponseScore: 0,
  verbalResponseScore: 0,
  motorResponseScore: 0,
  nihPositiveStrokeScaleScoreMap: new Map(),
  raceStrokeScaleScoreMap: new Map(),
  showRepeatVitals: false,
  reRunValidations: false,
  etCO2Type: null,
  clientEventId: null,
  vsAction: null,
  isEkgImport: null,
  showDateTimeToast: false,
  showPTAToast: false,
  __isPainRangeActivated: false,
  gcsQualifier: [],
  gcsQualifierPNNV: null,
  apgarAppearance: null,
  apgarPulse: null,
  apgarGrimace: null,
  apgarActivity: null,
  apgarRespiration: null,
  totalApgarScore: null,
  totalApgarScorePNNV: null,
  __manualApgarTotalChecked: false,
  apgar: null,
  __isRedirectedFrom: null,
  customVitalLabel1: '',
  customVitalLabel2: '',
  customVitalValue1: '',
  customVitalValue2: '',
  customVitalConfig: null,
  __customVitalLabelExists: false,
  painQuality: initialPainQualityState,
  shockIndex: '',
  sedationScale: initialSedationScaleState,
  withdrawalScaleCriteria: [],
  withdrawalScaleType: null,
  withdrawalScaleTotal: null,
  withdrawalScaleTotalPNNV: null,
  __isWithdrawalScaleTotalPNNVEnabled: false,
  __isWithdrawalScaleTypeCleared: false,
}

export const numericPainScaleType: IOption = {
  id: 0,
  name: 'Numeric',
  nemsisCode: '3328003',
}

export function vitalsReducer(state: IPatientVitals = initialVitalsState, action: IVitalsAction): IPatientVitals {
  switch (action.type) {
    case VitalsConstants.SET_DATE:
      return { ...state, date: action.date }
    case VitalsConstants.SET_TIME:
      return {
        ...state,
        time: action.time,
      }
    case VitalsConstants.ADD_CURRENT_TIME:
      return { ...state, time: getCurrentTime(), date: getCurrentDate() }
    case VitalsConstants.SET_PTA:
      return { ...state, pta: action.pta }
    case VitalsConstants.SET_ASSESSED_BY:
      return { ...state, assessedBy: action.assessedBy }
    case VitalsConstants.SET_COMMENTS:
      return { ...state, comments: action.comments }
    case VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE:
      return { ...state, systolicBP: action.systolicBP }
    case VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE_PNNV:
      return { ...state, systolicBPPNNV: action.systolicBPPNNV }
    case VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE:
      return { ...state, diastolicBP: action.diastolicBP }
    case VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE_PNNV:
      return { ...state, diastolicBPPNNV: action.diastolicBPPNNV }
    case VitalsConstants.SET_BLOOD_PRESSURE_METHOD:
      return { ...state, bloodPressureMethod: action.bloodPressureMethod }
    case VitalsConstants.SET_BLOOD_PRESSURE_METHOD_PNNV:
      return { ...state, bloodPressureMethodPNNV: action.bloodPressureMethodPNNV }
    case VitalsConstants.SET_MAP_VITAL:
      return {
        ...state,
        mapVital: action.mapVital,
      }
    case VitalsConstants.SET_HEART_RATE:
      return {
        ...state,
        heartRate: action.heartRate,
      }
    case VitalsConstants.SET_HEART_RATE_PNNV:
      return {
        ...state,
        heartRatePNNV: action.heartRatePNNV,
      }
    case VitalsConstants.SET_HEART_RATE_METHOD:
      return { ...state, heartRateMethod: action.heartRateMethod }
    case VitalsConstants.SET_SPO2:
      return {
        ...state,
        spo2: action.spo2,
      }
    case VitalsConstants.SET_SPO2_PNNV:
      return {
        ...state,
        spo2PNNV: action.spo2PNNV,
      }
    case VitalsConstants.SET_ROOM_AIR:
      return { ...state, roomAir: action.roomAir }
    case VitalsConstants.SET_RESPIRATION_RATE:
      return {
        ...state,
        respirationRate: action.respirationRate,
      }
    case VitalsConstants.SET_RESPIRATION_RATE_PNNV:
      return {
        ...state,
        respirationRatePNNV: action.respirationRatePNNV,
      }
    case VitalsConstants.SET_RESPIRATION_RATE_EFFORT:
      return { ...state, respirationRateEffort: action.respirationRateEffort }
    case VitalsConstants.SET_RHYTHM:
      return { ...state, rhythm: action.rhythm }
    case VitalsConstants.SET_RHYTHM_PNNV:
      return { ...state, rhythmPNNV: action.rhythmPNNV }
    case VitalsConstants.SET_REGULARITY:
      return { ...state, regularity: action.regularity }
    case VitalsConstants.SET_ECG_TYPE:
      return { ...state, ecgType: action.ecgType }
    case VitalsConstants.SET_ECG_TYPE_METHOD:
      return { ...state, ecgTypeMethod: action.ecgTypeMethod }
    case VitalsConstants.SET_TEMPERATURE:
      return {
        ...state,
        temperature: action.temperature,
      }
    case VitalsConstants.SET_TEMPERATURE_PNNV:
      return {
        ...state,
        temperaturePNNV: action.temperaturePNNV,
      }
    case VitalsConstants.SET_TEMPERATURE_TYPE:
      return { ...state, temperatureType: action.temperatureType }
    case VitalsConstants.SET_ETCO2_TYPE:
      return { ...state, etCO2Type: action.etCO2Type }
    case VitalsConstants.SET_TEMPERATURE_METHOD:
      return { ...state, temperatureMethod: action.temperatureMethod }
    case VitalsConstants.SET_SPCO:
      return {
        ...state,
        spCO: action.spCO,
      }
    case VitalsConstants.SET_SPCO_PNNV:
      return {
        ...state,
        spCOPNNV: action.spCOPNNV,
      }
    case VitalsConstants.SET_SPMET:
      return {
        ...state,
        spMet: action.spMet,
      }
    case VitalsConstants.SET_RTS:
      return { ...state, rts: action.rts }
    case VitalsConstants.SET_ETCO2:
      return {
        ...state,
        etCO2: action.etCO2,
      }
    case VitalsConstants.SET_ETCO2_PNNV:
      return {
        ...state,
        etCO2PNNV: action.etCO2PNNV,
      }
    case VitalsConstants.SET_GLUCOSE:
      return {
        ...state,
        glucose: action.glucose,
      }
    case VitalsConstants.SET_GLUCOSE_PNNV:
      return {
        ...state,
        glucosePNNV: action.glucosePNNV,
      }
    case VitalsConstants.SET_GLUCOSE_LEVEL:
      return { ...state, glucoseLevel: action.glucoseLevel }
    case VitalsConstants.SET_LEVEL_OF_CONSCIOUSNESS:
      return { ...state, levelOfConsciousness: action.levelOfConsciousness }
    case VitalsConstants.SET_PAIN:
      return { ...state, pain: action.pain }
    case VitalsConstants.SET_PAIN_PNNV:
      return { ...state, painPNNV: action.painPNNV }
    case VitalsConstants.SET_PAIN_TYPE:
      return { ...state, painType: action.painType }
    case VitalsConstants.SET_PAIN_QUALITY:
      return { ...state, painQuality: action.painQuality }
    case VitalsConstants.SET_PAIN_QUALITY_ONSET:
      return {
        ...state,
        painQuality: { ...(state.painQuality || initialPainQualityState), onset: action.onset },
      }
    case VitalsConstants.SET_PAIN_QUALITY_ONSET_UNIT:
      return {
        ...state,
        painQuality: { ...(state.painQuality || initialPainQualityState), onsetUnit: action.onsetUnit },
      }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD1:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field1: action.field1 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD2:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field2: action.field2 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD3:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field3: action.field3 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD4:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field4: action.field4 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD5:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field5: action.field5 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD6:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field6: action.field6 } }
    case VitalsConstants.SET_PAIN_QUALITY_FIELD7:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), field7: action.field7 } }
    case VitalsConstants.SET_PAIN_QUALITY_COMMENTS:
      return { ...state, painQuality: { ...(state.painQuality || initialPainQualityState), comments: action.comments } }
    case VitalsConstants.SET_PAIN_SCALES:
      return { ...state, painScales: action.painScales }
    case VitalsConstants.SET_SEDATION_SCALE:
      return { ...state, sedationScale: action.sedationScale }
    case VitalsConstants.SET_STROKE_SCALE:
      return {
        ...state,
        strokeScale: action.strokeScale,
        strokeScoreFlag: action.strokeScoreFlag,
      }
    case VitalsConstants.SET_STROKE_SCALE_PNNV:
      return {
        ...state,
        strokeScalePNNV: action.strokeScalePNNV,
      }
    case VitalsConstants.SET_STROKE_SCALE_SCORE_TOTAL:
      return { ...state, strokeScaleScoreTotal: action.strokeScaleScoreTotal }
    case VitalsConstants.SET_VITALS_STROKE_SCALE_VALUES:
      return { ...state, vitalsStrokeScaleValues: action.vitalsStrokeScaleValues }
    case VitalsConstants.SET_STROKE_SCALE_SCORE_TOTAL_PNNV:
      return { ...state, strokeScaleScoreTotalPNNV: action.strokeScaleScoreTotalPNNV }
    case VitalsConstants.SET_STROKE_SCALE_SCORE_TOTAL_PNNV_ENABLED:
      return { ...state, __isStrokeScaleScoreTotalPNNVEnabled: action.__isStrokeScaleScoreTotalPNNVEnabled }
    case VitalsConstants.SET_STATE:
      return { ...state, ...action.vitalState, reRunValidations: true, showRepeatVitals: false }
    case VitalsConstants.SET_EYE_OPENING_RESPONSE:
      return { ...state, eyeOpeningResponse: action.eyeOpeningResponse }
    case VitalsConstants.SET_EYE_OPENING_RESPONSE_PNNV:
      return { ...state, eyeOpeningResponsePNNV: action.eyeOpeningResponsePNNV }
    case VitalsConstants.SET_EYE_OPENING_RESPONSE_SCORE:
      return { ...state, eyeOpeningResponseScore: action.eyeOpeningResponseScore }
    case VitalsConstants.SET_VERBAL_RESPONSE:
      return { ...state, verbalResponse: action.verbalResponse }
    case VitalsConstants.SET_VERBAL_RESPONSE_PNNV:
      return { ...state, verbalResponsePNNV: action.verbalResponsePNNV }
    case VitalsConstants.SET_VERBAL_RESPONSE_SCORE:
      return { ...state, verbalResponseScore: action.verbalResponseScore }
    case VitalsConstants.SET_MOTOR_RESPONSE:
      return { ...state, motorResponse: action.motorResponse }
    case VitalsConstants.SET_MOTOR_RESPONSE_PNNV:
      return { ...state, motorResponsePNNV: action.motorResponsePNNV }
    case VitalsConstants.SET_MOTOR_RESPONSE_SCORE:
      return { ...state, motorResponseScore: action.motorResponseScore }
    case VitalsConstants.SET_PROTOCOL:
      return { ...state, protocol: action.protocol }
    case VitalsConstants.SET_TOTAL_GSC_SCORE:
      return { ...state, totalGCSScore: action.totalGCSScore }
    case VitalsConstants.SET_TOTAL_GSC_SCORE_PNNV:
      return { ...state, totalGCSScorePNNV: action.totalGCSScorePNNV }
    case VitalsConstants.SET_NIH_POSITIVE_STROKE_SCALE_SCORE_MAP:
      return { ...state, nihPositiveStrokeScaleScoreMap: action.nihPositiveStrokeScaleScoreMap }
    case VitalsConstants.SET_RACE_STROKE_SCALE_SCORE_MAP:
      return { ...state, raceStrokeScaleScoreMap: action.raceStrokeScaleScoreMap }
    case VitalsConstants.SET_SHOW_REPEAT_VITALS:
      return { ...state, showRepeatVitals: action.showRepeatVitals }
    case VitalsConstants.SET_SHOW_DATE_TIME_TOAST:
      return { ...state, showDateTimeToast: action.showDateTimeToast }
    case VitalsConstants.SET_SHOW_PTA_TOAST:
      return { ...state, showPTAToast: action.showPTAToast }
    case VitalsConstants.SET_IS_PAIN_RANGE_ACTIVATED:
      return { ...state, __isPainRangeActivated: action.__isPainRangeActivated }
    case VitalsConstants.SET_QUALIFIER:
      return { ...state, gcsQualifier: action.gcsQualifier }
    case VitalsConstants.SET_QUALIFIER_PNNV:
      return { ...state, gcsQualifierPNNV: action.gcsQualifierPNNV }
    case VitalsConstants.SET_APGAR_APPEARANCE:
      return { ...state, apgarAppearance: action.apgarAppearance }
    case VitalsConstants.SET_APGAR_PULSE:
      return { ...state, apgarPulse: action.apgarPulse }
    case VitalsConstants.SET_APGAR_GRIMACE:
      return { ...state, apgarGrimace: action.apgarGrimace }
    case VitalsConstants.SET_APGAR_ACTIVITY:
      return { ...state, apgarActivity: action.apgarActivity }
    case VitalsConstants.SET_APGAR_RESPIRATION:
      return { ...state, apgarRespiration: action.apgarRespiration }
    case VitalsConstants.SET_TOTAL_APGAR_SCORE:
      return { ...state, totalApgarScore: action.totalApgarScore }
    case VitalsConstants.SET_TOTAL_APGAR_SCORE_PNNV:
      return { ...state, totalApgarScorePNNV: action.totalApgarScorePNNV }
    case VitalsConstants.SET_APGAR:
      return { ...state, apgar: action.apgar }
    case VitalsConstants.SET_MANUAL_APGAR_TOTAL_CHECKED:
      return { ...state, __manualApgarTotalChecked: action.__manualApgarTotalChecked }
    case VitalsConstants.SET_IS_REDIRECTED_FROM:
      return { ...state, __isRedirectedFrom: action.__isRedirectedFrom }
    case VitalsConstants.SET_CUSTOM_VITAL_LABEL1:
      return { ...state, customVitalLabel1: action.customVitalLabel1 }
    case VitalsConstants.SET_CUSTOM_VITAL_LABEL2:
      return { ...state, customVitalLabel2: action.customVitalLabel2 }
    case VitalsConstants.SET_CUSTOM_VITAL_VALUE1:
      return { ...state, customVitalValue1: action.customVitalValue1 }
    case VitalsConstants.SET_CUSTOM_VITAL_VALUE2:
      return { ...state, customVitalValue2: action.customVitalValue2 }
    case VitalsConstants.SET_CUSTOM_VITAL_LABEL_EXISTS:
      return { ...state, __customVitalLabelExists: action.__customVitalLabelExists }
    case VitalsConstants.SET_SHOCK_INDEX:
      return { ...state, shockIndex: action.shockIndex }
    case VitalsConstants.SET_WITHDRAWAL_SCALE_TYPE:
      return { ...state, withdrawalScaleType: action.withdrawalScaleType }
    case VitalsConstants.SET_WITHDRAWAL_SCALE_TYPE_CLEARED:
      return { ...state, __isWithdrawalScaleTypeCleared: action.__isWithdrawalScaleTypeCleared }
    case VitalsConstants.SET_WITHDRAWAL_SCALES:
      return { ...state, withdrawalScaleCriteria: action.withdrawalScaleCriteria }
    case VitalsConstants.SET_WITHDRAWAL_SCALES_TOTAL:
      return { ...state, withdrawalScaleTotal: action.withdrawalScaleTotal }
    case VitalsConstants.SET_WITHDRAWAL_SCALES_TOTAL_PNNV:
      return { ...state, withdrawalScaleTotalPNNV: action.withdrawalScaleTotalPNNV }
    case VitalsConstants.SET_WITHDRAWAL_SCALES_TOTAL_PNNV_ENABLED:
      return { ...state, __isWithdrawalScaleTotalPNNVEnabled: action.__isWithdrawalScaleTotalPNNVEnabled }
    case VitalsConstants.RESET_APGAR_FIELDS:
      return {
        ...state,
        apgarAppearance: null,
        apgarPulse: null,
        apgarGrimace: null,
        apgarActivity: null,
        apgarRespiration: null,
        totalApgarScorePNNV: null,
        apgar: null,
        __manualApgarTotalChecked: false,
      }
    case VitalsConstants.RESET_PATIENT_VITALS_STATE:
      return {
        key: randomNumber().toString(),
        pta: false,
        date: '',
        time: '',
        assessedBy: null,
        comments: '',
        systolicBP: null,
        systolicBPPNNV: null,
        diastolicBP: null,
        diastolicBPPNNV: null,
        bloodPressureMethod: null,
        bloodPressureMethodPNNV: null,
        mapVital: null,
        heartRate: null,
        heartRatePNNV: null,
        heartRateMethod: null,
        spo2: null,
        spo2PNNV: null,
        roomAir: false,
        respirationRate: null,
        respirationRatePNNV: null,
        respirationRateEffort: null,
        rhythm: [],
        rhythmPNNV: null,
        regularity: null,
        ecgType: null,
        ecgTypeMethod: [],
        ecgTypeMethodPNNV: null,
        temperature: null,
        temperaturePNNV: null,
        temperatureType: '',
        temperatureMethod: null,
        spCO: null,
        spCOPNNV: null,
        spMet: null,
        rts: null,
        etCO2: null,
        etCO2PNNV: null,
        glucose: null,
        glucosePNNV: null,
        glucoseLevel: 'Low',
        levelOfConsciousness: null,
        pain: '',
        painPNNV: null,
        painType: null,
        painScales: { totalsCombined: '', painScaleSelections: [] },
        strokeScale: null,
        strokeScalePNNV: null,
        strokeScoreFlag: false,
        strokeScaleScoreTotal: null,
        vitalsStrokeScaleValues: [],
        strokeScaleScoreTotalPNNV: null,
        __isStrokeScaleScoreTotalPNNVEnabled: false,
        eyeOpeningResponse: null,
        eyeOpeningResponsePNNV: null,
        verbalResponse: null,
        verbalResponsePNNV: null,
        motorResponse: null,
        motorResponsePNNV: null,
        protocol: null,
        totalGCSScore: null,
        totalGCSScorePNNV: null,
        eyeOpeningResponseScore: 0,
        verbalResponseScore: 0,
        motorResponseScore: 0,
        nihPositiveStrokeScaleScoreMap: new Map(),
        raceStrokeScaleScoreMap: new Map(),
        showRepeatVitals: false,
        reRunValidations: false,
        etCO2Type: null,
        clientEventId: null,
        vsAction: null,
        isEkgImport: null,
        showDateTimeToast: false,
        showPTAToast: false,
        __isPainRangeActivated: false,
        gcsQualifier: [],
        gcsQualifierPNNV: null,
        apgarAppearance: null,
        apgarPulse: null,
        apgarGrimace: null,
        apgarActivity: null,
        apgarRespiration: null,
        totalApgarScore: null,
        totalApgarScorePNNV: null,
        __manualApgarTotalChecked: false,
        apgar: null,
        __isRedirectedFrom: null,
        customVitalLabel1: '',
        customVitalLabel2: '',
        customVitalValue1: '',
        customVitalValue2: '',
        customVitalConfig: null,
        __customVitalLabelExists: false,
        painQuality: {
          onset: null,
          onsetUnit: null,
          field1: null,
          field2: null,
          field3: null,
          field4: null,
          field5: null,
          field6: null,
          field7: null,
          comments: '',
        },
        shockIndex: '',
        sedationScale: { ...initialSedationScaleState },
        withdrawalScaleCriteria: [],
        withdrawalScaleType: null,
        withdrawalScaleTotal: null,
        withdrawalScaleTotalPNNV: null,
        __isWithdrawalScaleTotalPNNVEnabled: false,
        __isWithdrawalScaleTypeCleared: false,
      }
    default:
      return state
  }
}
